"use client";

import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import type { MatchedHospital } from "@/types/api";

interface Props {
  hospitals: MatchedHospital[];
}

/** 카카오맵 SDK 최소 타입 (공식 타입 미제공) */
interface KakaoMaps {
  maps: {
    load: (callback: () => void) => void;
    LatLng: new (lat: number, lng: number) => object;
    LatLngBounds: new () => { extend: (latlng: object) => void };
    Map: new (
      el: HTMLElement,
      options: { center: object; level: number },
    ) => { setBounds: (bounds: object) => void };
    Marker: new (options: { position: object; map: object }) => object;
    InfoWindow: new (options: { content: string }) => {
      open: (map: object, marker: object) => void;
    };
    event: { addListener: (target: object, type: string, handler: () => void) => void };
  };
}

declare global {
  interface Window {
    kakao: KakaoMaps;
  }
}

export function HospitalsMap({ hospitals }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const withCoords = hospitals.filter((h) => h.lat && h.lng);

  useEffect(() => {
    if (!showMap || withCoords.length === 0) return;

    if (window.kakao?.maps) {
      // SDK가 이미 로드된 상태 — 다음 tick에 마크(동기 setState 회피)
      const raf = requestAnimationFrame(() => setMapLoaded(true));
      return () => cancelAnimationFrame(raf);
    }

    const key = process.env.NEXT_PUBLIC_KAKAO_MAP_KEY;
    if (!key) return;

    const script = document.createElement("script");
    script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${key}&autoload=false`;
    script.onload = () => {
      window.kakao.maps.load(() => setMapLoaded(true));
    };
    document.head.appendChild(script);
  }, [showMap, withCoords.length]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || withCoords.length === 0) return;

    const { kakao } = window;
    const center = new kakao.maps.LatLng(withCoords[0].lat, withCoords[0].lng);
    const map = new kakao.maps.Map(mapRef.current, {
      center,
      level: 8,
    });

    const bounds = new kakao.maps.LatLngBounds();
    withCoords.forEach((h) => {
      const position = new kakao.maps.LatLng(h.lat, h.lng);
      const marker = new kakao.maps.Marker({ position, map });
      bounds.extend(position);

      const info = new kakao.maps.InfoWindow({
        content: `<div style="padding:8px 12px;font-size:13px;white-space:nowrap"><b>${h.name}</b>${h.sggu ? `<br/><span style="color:#888;font-size:11px">${h.sido} ${h.sggu}</span>` : ""}</div>`,
      });
      kakao.maps.event.addListener(marker, "click", () => {
        info.open(map, marker);
      });
    });

    if (withCoords.length > 1) {
      map.setBounds(bounds);
    }
  }, [mapLoaded, withCoords]);

  if (withCoords.length === 0) return null;

  return (
    <div className="mt-4">
      {!showMap ? (
        <button
          onClick={() => setShowMap(true)}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
        >
          <MapPin className="h-4 w-4" />
          지도에서 보기 ({withCoords.length}곳)
        </button>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <div ref={mapRef} style={{ width: "100%", height: "400px" }} />
        </div>
      )}
    </div>
  );
}
